import React, { useEffect, useMemo, useState } from 'react';
import { EmptyState, ErrorNote } from '../ui';

interface CalEntry {
  id: string;
  publishAt: string;
  method: string;
  status: string;
  pageLabel: string;
  timeZone: string;
  contentId: string;
  title: string | null;
  contentType: string;
  state: string;
}

interface PageOption { id: string; internalLabel: string; timeZone: string }

const STATUS_COLOR: Record<string, string> = {
  PLANNED: 'border-l-sky-500', ARMED: 'border-l-brand-400', EXECUTING: 'border-l-amber-400',
  DONE: 'border-l-emerald-500', MISSED: 'border-l-red-500', CANCELLED: 'border-l-neutral-600',
};

function startOfWeek(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  copy.setDate(copy.getDate() - copy.getDay());
  return copy;
}

function wallDateTimeValue(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '00';
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`;
}

function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function CalendarScreen({ pageId }: { pageId?: string } = {}) {
  const [entries, setEntries] = useState<CalEntry[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [localFilter, setLocalFilter] = useState('');
  const pageFilter = pageId ?? localFilter;
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [error, setError] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTime, setEditingTime] = useState('');
  const displayTimeZone = pages.find((page) => page.id === pageFilter)?.timeZone ?? entries[0]?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const weekEnd = useMemo(() => new Date(weekStart.getTime() + 7 * 24 * 3600 * 1000), [weekStart]);

  const load = async () => {
    try {
      setPages((await window.pagepilot.pages.list()) as PageOption[]);
      const rows = (await window.pagepilot.calendar.range(weekStart.toISOString(), weekEnd.toISOString(), pageFilter || undefined)) as CalEntry[];
      setEntries(rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [weekStart, pageFilter]);

  const moveToDay = async (day: Date) => {
    const entry = entries.find((e) => e.id === draggingId);
    if (!entry || entry.status !== 'PLANNED') return;
    const currentWall = wallDateTimeValue(entry.publishAt, entry.timeZone);
    const nextWall = `${localDateKey(day)}T${currentWall.slice(11)}`;
    if (nextWall === currentWall) return;
    try {
      await window.pagepilot.calendar.rescheduleLocal(entry.id, nextWall);
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDraggingId(null);
    }
  };

  const saveTime = async (entry: CalEntry) => {
    try {
      await window.pagepilot.calendar.rescheduleLocal(entry.id, editingTime);
      setEditingId(null);
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * 24 * 3600 * 1000)),
    [weekStart],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Content Calendar</h1>
        <button className="rounded-lg bg-neutral-800 px-2 py-1 text-sm hover:bg-neutral-700" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 864e5))}>←</button>
        <button className="rounded-lg bg-neutral-800 px-3 py-1 text-sm hover:bg-neutral-700" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>
        <button className="rounded-lg bg-neutral-800 px-2 py-1 text-sm hover:bg-neutral-700" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 864e5))}>→</button>
        <span className="text-sm text-neutral-500">{weekStart.toLocaleDateString()} – {new Date(weekEnd.getTime() - 864e5).toLocaleDateString()}</span>
        <span className="text-xs text-neutral-500" title="Publish times below are shown in this time zone">🕑 {displayTimeZone}</span>
        {!pageId && (
          <select value={localFilter} onChange={(e) => setLocalFilter(e.target.value)} className="ml-auto rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm">
            <option value="">All Pages</option>
            {pages.map((p) => <option key={p.id} value={p.id}>{p.internalLabel}</option>)}
          </select>
        )}
      </div>
      {error && <ErrorNote message={error} />}

      <div className="flex flex-wrap gap-2 text-[11px] text-neutral-500">
        <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-sky-500" />Planned</span>
        <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-brand-400" />Armed</span>
        <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />Published</span>
        <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />Missed</span>
        <span className="ml-2">▣ Reel · ◻ Image</span>
      </div>

      {entries.length === 0 ? (
        <EmptyState title="Nothing scheduled this week" hint="Approved content lands here once scheduled. Use the arrows to browse other weeks." />
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {days.map((day) => {
            const dayEntries = entries.filter((e) => wallDateTimeValue(e.publishAt, e.timeZone).slice(0, 10) === localDateKey(day));
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <div
                key={day.toISOString()}
                onDragOver={(e) => { if (draggingId) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); void moveToDay(day); }}
                className={`min-h-40 rounded-lg border p-2 ${isToday ? 'border-brand-500/50 bg-brand-500/5' : 'border-neutral-800 bg-neutral-900/40'}`}
              >
                <p className="mb-2 text-xs font-medium text-neutral-400">
                  {day.toLocaleDateString(undefined, { weekday: 'short' })} {day.getDate()}
                </p>
                <div className="space-y-1.5">
                  {dayEntries.map((e) => (
                    <div
                      key={e.id}
                      draggable={e.status === 'PLANNED'}
                      onDragStart={() => {
                        if (e.status === 'PLANNED') setDraggingId(e.id);
                      }}
                      onDragEnd={() => setDraggingId(null)}
                      title={e.status === 'PLANNED' ? 'Drag to another day to reschedule' : 'Only planned schedules can be moved'}
                      className={`rounded border-l-2 bg-neutral-800/60 p-1.5 ${e.status === 'PLANNED' ? 'cursor-grab active:cursor-grabbing' : ''} ${STATUS_COLOR[e.status] ?? 'border-l-neutral-600'}`}
                    >
                      <p className="flex items-center gap-1 text-[11px] text-neutral-300">
                        <span>{e.contentType === 'REEL' ? '▣' : '◻'}</span>
                        <span className="tabular-nums text-neutral-500">{new Date(e.publishAt).toLocaleTimeString([], { timeZone: e.timeZone, hour: '2-digit', minute: '2-digit' })}</span>
                      </p>
                      <p className="truncate text-[11px] text-neutral-200">{e.title ?? 'Untitled'}</p>
                      <p className="truncate text-[10px] text-neutral-500">{e.pageLabel} · {e.method === 'FACEBOOK_NATIVE' ? 'FB native' : 'PagePilot'}</p>
                      {e.status === 'PLANNED' && (
                        <button
                          className="mt-1 text-[10px] text-brand-300 hover:underline"
                          onClick={() => { setEditingId(e.id); setEditingTime(wallDateTimeValue(e.publishAt, e.timeZone)); }}
                        >
                          Adjust date & time
                        </button>
                      )}
                      {editingId === e.id && (
                        <div className="mt-2 space-y-1" onClick={(event) => event.stopPropagation()}>
                          <input type="datetime-local" value={editingTime} onChange={(event) => setEditingTime(event.target.value)} className="w-full rounded border border-neutral-600 bg-neutral-950 px-1 py-1 text-[10px]" />
                          <div className="flex gap-2">
                            <button className="text-[10px] text-brand-300 hover:underline" onClick={() => void saveTime(e)}>Save</button>
                            <button className="text-[10px] text-neutral-500 hover:underline" onClick={() => setEditingId(null)}>Cancel</button>
                          </div>
                          <p className="text-[9px] text-neutral-500">Time is in {e.timeZone}. Facebook-native posts require 20 minutes ahead.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
